import { useState, useEffect, useCallback } from "react";
import { rarityInfo } from "../lib/rarity";
import { Loader2, CheckCircle2, Star, ExternalLink, Paintbrush, Sparkles, Pencil, RotateCcw, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AddToListButtons } from "./AddToListButtons";
import { PriceHistoryChart } from "./PriceHistoryChart";
import { computeVariants, variantStyle, isCustomVariant } from "../lib/variants";
import { secondaryPriceLabel } from "../lib/prices";
import { getCollectionKey } from "../lib/collection";
import { VARIANT_ORDER, LABELS, CONDITION_GRADES } from "../config/variants";
import { fetchCard, fetchCardPriceHistory, setCardVariantOverride, clearCardVariantOverride } from "../lib/api";
import { CardImage } from "./CardImage";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
// Bloc « Marché réel » (Œil de Sauron) : prix réel en chiffre principal,
// cote en référence, évolution réel vs cote — cf. RealMarketBlock.jsx.
import { RealMarketBlock, useRealMarketData } from "./RealMarketBlock";

export const CardDetailModal = ({
  cardId, isOpen, onClose, siblingIds = null, onNavigate,
  collection, conditions = {}, onStatusChange, onRemove, onUpdateCondition,
  lists = [], onCreateList, onAddToList,
  customVariants = [], customApplications = [], onCreateCustomVariant,
  onApplyCustomVariant, onRemoveCustomApplication,
  customVariantsError = null, onReloadCustomVariants,
  masterRules = [], onAddMasterRule, onRemoveMasterRule,
  // Rebonds vers la vue "Explorer les cartes" : nom du Pokémon ou illustrateur.
  onBrowseName, onBrowseIllustrator,
  // Rebond vers l'onglet Œil de Sauron (toutes les cartes observées).
  onOpenOeil,
  // Vue en lecture seule (collection d'un ami) : masque tous les contrôles
  // d'écriture, ne garde que la consultation (variantes, prix, historique).
  readOnly = false,
}) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVariantKey, setSelectedVariantKey] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editSelected, setEditSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [cvNewName, setCvNewName] = useState("");
  const [cvBusy, setCvBusy] = useState(false);
  const [history, setHistory] = useState([]);
  // Marché réel (Œil de Sauron) : chargé une fois ici, affiché en deux
  // morceaux (résumé à droite, courbe à gauche sous le tableau Cardmarket).
  const realMarket = useRealMarketData(isOpen ? cardId : null);
  const online = useNetworkStatus();

  // Navigation suivante/précédente dans la liste affichée (si fournie).
  const navIndex = siblingIds ? siblingIds.indexOf(cardId) : -1;
  const prevId = navIndex > 0 ? siblingIds[navIndex - 1] : null;
  const nextId = navIndex >= 0 && navIndex < siblingIds.length - 1 ? siblingIds[navIndex + 1] : null;

  const loadCard = useCallback((id) => {
    setLoading(true);
    return fetchCard(id)
      .then((cardData) => {
        setCard(cardData);
        const variants = computeVariants(cardData);
        setSelectedVariantKey(variants[0]?.key || null);
        return cardData;
      })
      .catch(err => console.error("Error fetching card:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!cardId || !isOpen) return;
    setCard(null);
    setEditMode(false);
    setHistory([]);
    // On charge la fiche PUIS l'historique : le relevé du jour est créé côté
    // serveur pendant get_card, donc l'attendre garantit qu'il figure déjà dans
    // la courbe. Non bloquant pour l'affichage de la fiche.
    loadCard(cardId).then(() =>
      fetchCardPriceHistory(cardId).then(setHistory).catch(() => setHistory([]))
    );
  }, [cardId, isOpen, loadCard]);

  // Garde la barre de navigation basse visible PAR-DESSUS la fiche (plutôt
  // que masquée derrière, comme les autres Dialog) : demande explicite pour
  // rester orienté pendant la consultation d'une carte. Scopé à CETTE fiche
  // uniquement (classe posée/retirée avec elle) - les autres Dialog (Réglages
  // etc.) gardent le comportement d'origine. cf. `pb-24` plus bas, qui évite
  // que ça recouvre les boutons "Ajouter à :".
  useEffect(() => {
    document.body.classList.toggle("card-modal-open", isOpen);
    return () => document.body.classList.remove("card-modal-open");
  }, [isOpen]);

  // Flèches clavier ← / → pour naviguer entre les cartes.
  useEffect(() => {
    if (!isOpen || !onNavigate) return;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft" && prevId) onNavigate(prevId);
      else if (e.key === "ArrowRight" && nextId) onNavigate(nextId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onNavigate, prevId, nextId]);

  const startEdit = () => {
    setEditSelected(new Set(computeVariants(card).filter(v => v.type !== "loading").map(v => v.type)));
    setEditMode(true);
  };

  const toggleEditType = (type) => {
    setEditSelected(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const saveOverride = async () => {
    setSaving(true);
    try {
      await setCardVariantOverride(card.id, Array.from(editSelected));
      await loadCard(card.id);
      setEditMode(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const resetOverride = async () => {
    setSaving(true);
    try {
      await clearCardVariantOverride(card.id);
      await loadCard(card.id);
      setEditMode(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const variants = computeVariants(card);
  const selectedVariant = variants.find(v => v.key === selectedVariantKey) || variants[0];
  const selectedKey = card && selectedVariant ? getCollectionKey(card.id, selectedVariant.type) : null;
  const variantStatus = selectedKey ? collection[selectedKey] : null;
  const variantCondition = selectedKey ? conditions[selectedKey] : null;
  const cardmarket = card?.pricing?.cardmarket;
  // Un « 0 » de Cardmarket veut dire « pas de cote », pas « gratuit » : on
  // affiche un tiret plutôt qu'un trompeur 0,00 €.
  const eur = (v) => (!v ? "—" : `${v.toFixed(2)}€`);
  // La ligne Reverse n'apparaît que si Cardmarket a au moins un prix "holo"
  // NON NUL — sinon on affichait une ligne entière de 0,00 € pour les cartes
  // qui n'existent tout simplement pas en reverse.
  const hasReverse = !!cardmarket && [
    cardmarket["trend-holo"], cardmarket["avg-holo"],
    cardmarket["avg1-holo"], cardmarket["avg7-holo"], cardmarket["avg30-holo"],
  ].some(v => !!v);
  // Étiquette du 2e niveau de prix, déduite des variantes RÉELLES de la carte
  // (card.variants, source TCGdex) — jamais du prix seul. Une carte à
  // Première Édition (vieux tirage WOTC) n'a pas de reverse : son champ
  // `-holo` sert de support à la cote 1re Édition, pas à une reverse holo.
  const variantFlags = card?.variants || {};
  const secondaryKey = card?.variants_locked ? "rgb" : secondaryPriceLabel(
    Object.entries(variantFlags).filter(([, v]) => v).map(([k]) => k)
  );
  const SECONDARY_LABEL = { reverse: "Reverse", firstEdition: "Première Édition", holo: "Holo", cosmos: "Holo Cosmos", rgb: "RGB" };
  const SECONDARY_TEXT_CLASS = {
    reverse: "text-sky-400", firstEdition: "text-amber-400", holo: "text-purple-400", cosmos: "text-fuchsia-400", rgb: "text-emerald-400",
  };
  const SECONDARY_HEX = { reverse: "#38bdf8", firstEdition: "#fbbf24", holo: "#c084fc", cosmos: "#e879f9", rgb: "#34d399" };

  const addCurrentToList = async (listId) => {
    if (!card || !selectedVariant) return;
    await onAddToList(listId, [{ card_id: card.id, variant: selectedVariant.type }]);
  };

  const changeCondition = (grade) => {
    if (card && selectedVariant) onUpdateCondition(card.id, selectedVariant.type, grade);
  };

  // ── Variantes perso sur CETTE carte ──
  // Une variante peut être active sur la carte de deux façons : via une
  // application "carte" (qu'on peut retirer ici) ou via une règle de set
  // (héritée — se gère depuis la collection, pas ici).
  const cardCustomIds = new Set((card?.custom_variants || []).map(cv => cv.id));
  const catalogBroken = !!customVariantsError || (card?.custom_variants || []).some(cv => !customVariants.some(v => v.id === cv.id));
  const cardApp = (variantId) => customApplications.find(
    a => a.scope === "card" && a.card_id === card?.id && a.variant_id === variantId
  );

  // ── Réservé au Master Set + sur CETTE carte ──
  // Une réservation peut venir d'une règle de la carte (retirable ici) ou d'une
  // règle plus large, posée sur le set ou partout (affichée, retirable dans le set).
  const masterOnly = card?.master_plus_only || [];
  const cardMasterRule = (variant) => masterRules.find(r =>
    r.card_id === card?.id && (r.variant || null) === variant);
  const toggleMasterRule = async (variant) => {
    if (!card) return;
    setCvBusy(true);
    try {
      const rule = cardMasterRule(variant);
      if (rule) await onRemoveMasterRule(rule.id);
      else await onAddMasterRule({ card_id: card.id, variant });
      await loadCard(card.id);
    } catch (err) { console.error(err); }
    finally { setCvBusy(false); }
  };
  const masterChip = (variant, label) => {
    const active = masterOnly.includes(variant || "*");
    const own = !!cardMasterRule(variant);
    const inherited = active && !own;
    return (
      <button key={variant || "*"} disabled={cvBusy || inherited}
        onClick={() => toggleMasterRule(variant)}
        title={inherited
          ? "Vient d'une règle plus large : se retire dans le set, « Réservé au Master Set + »"
          : active ? "Remettre dans le Master Set" : "Ne compter qu'en Master Set +"}
        className={`variant-chip ${active ? "bg-amber-600 ring-2 ring-white/60" : "bg-zinc-800 opacity-60"} ${inherited ? "cursor-not-allowed" : ""}`}>
        {label}{inherited ? " (règle)" : ""}
      </button>
    );
  };

  const toggleCardCustomVariant = async (variant) => {
    if (!card) return;
    setCvBusy(true);
    try {
      const app = cardApp(variant.id);
      if (app) {
        await onRemoveCustomApplication(app.id);
      } else {
        if (cardCustomIds.has(variant.id)) return; // héritée d'une règle de set
        await onApplyCustomVariant(variant.id, { scope: "card", cardId: card.id });
      }
      await loadCard(card.id);
    } catch (err) { console.error(err); }
    finally { setCvBusy(false); }
  };

  const createCustomForCard = async () => {
    const name = cvNewName.trim();
    if (!name || !card || !onCreateCustomVariant) return;
    setCvBusy(true);
    try {
      const created = await onCreateCustomVariant({ name });
      if (created?.id) {
        await onApplyCustomVariant(created.id, { scope: "card", cardId: card.id });
        await loadCard(card.id);
      }
      setCvNewName("");
    } catch (err) { console.error(err); }
    finally { setCvBusy(false); }
  };

  return (
    // modal={false} : par defaut, Radix rend le reste de la page non-interactif
    // (pointer-events bloques au niveau DOM, pas juste visuellement en dessous)
    // tant que le Dialog est ouvert - MEME si un element comme la barre de
    // navigation basse est peint PAR-DESSUS via z-index (cf. body.card-modal-open
    // dans App.css). Symptome observe : un premier clic sur la barre fermait la
    // fiche (Radix detecte quand meme le pointerdown "exterieur" pour la
    // fermeture) sans jamais atteindre le onClick du bouton, qui n'agissait
    // qu'au second clic une fois la fiche reellement fermee. Cette fiche est la
    // SEULE a garder la barre visible par-dessus (les autres Dialog - Reglages,
    // etc. - la laissent cachee derriere comme avant), donc c'est le seul
    // endroit ou ce changement est necessaire.
    <Dialog open={isOpen} onOpenChange={onClose} modal={false}>
      {/* `max-w-5xl` seul laissait le contenu prendre sa largeur MINIMALE
          intrinseque (tableau des prix, courbe SVG, boutons non secables) : sur
          telephone elle depasse l'ecran, et `overflow-hidden` la rognait sans
          rien laisser defiler — d'ou la fiche illisible et decalee. On borne
          donc explicitement a la largeur de l'ecran en dessous de `sm`. */}
      <DialogContent className="card-modal-sheet bg-zinc-900 border-zinc-800 w-full max-w-[100vw] sm:max-w-5xl max-h-[85vh] sm:max-h-[92vh] overflow-hidden p-0 rounded-none sm:rounded-lg">
        {onNavigate && prevId && (
          <button onClick={() => onNavigate(prevId)} title="Carte précédente (←)"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center border border-zinc-700 shadow-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {onNavigate && nextId && (
          <button onClick={() => onNavigate(nextId)} title="Carte suivante (→)"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white flex items-center justify-center border border-zinc-700 shadow-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        {/* Defilement natif plutot que Radix ScrollArea : celle-ci enveloppe le
            contenu dans un `display: table` qui grossit jusqu'a sa largeur
            max-content au lieu de s'en tenir a celle du modal. */}
        <div className="card-modal-sheet max-h-[85vh] sm:max-h-[92vh] overflow-y-auto overflow-x-hidden overscroll-contain">
          {/* pb-12 en plus du padding normal : sur telephone, la barre d'adresse
              du navigateur + la barre de navigation Android (boutons ou geste)
              rognent le bas du modal sans que `92vh` en tienne compte - la
              derniere section ("Ajouter a :") se retrouvait plaquee contre le
              bord et invisible/inaccessible. Cette marge laisse de quoi
              scroller au-dela, meme quand le systeme grignote le viewport.
              (Le chevauchement avec la barre de navigation basse - restee
              visible PAR-DESSUS la fiche, cf. body.card-modal-open dans
              App.css - est gere separement par `.card-modal-sheet` dans
              App.css, qui reduit la hauteur MAX de la fiche : un simple
              padding en fin de contenu ne suffisait pas, verifie sur
              appareil reel, car la fiche est centree verticalement et son
              bord bas tombe pile derriere la barre EN PERMANENCE, pas
              seulement en toute fin de defilement.) */}
          <div className="p-4 sm:p-6 pb-12 sm:pb-6">
            {loading ? (
              <>
                <DialogHeader><DialogTitle className="sr-only">Chargement</DialogTitle></DialogHeader>
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 animate-spin text-red-500" />
                </div>
              </>
            ) : card ? (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3 flex-wrap">
                    {/* On cherche l'ESPÈCE, pas le nom imprimé : « Mystherbe
                        d'Erika » ou « M-Lucario-ex » ne renvoyaient qu'à
                        eux-mêmes. `species_name` vient du dexId côté backend et
                        n'est présent que s'il diffère du nom de la carte. */}
                    {onBrowseName ? (
                      <button className="hover:text-red-400 hover:underline"
                        title={card.species_name
                          ? `Voir toutes les cartes de ${card.species_name}`
                          : `Voir toutes les cartes de ${card.name}`}
                        onClick={() => onBrowseName(card.species_name || card.name)}>
                        {card.name}
                      </button>
                    ) : card.name}
                    {card.hp && <span className="text-sm font-normal text-red-400 bg-red-500/10 px-2 py-0.5 rounded">{card.hp} PV</span>}
                    {card.rarity && (() => { const r = rarityInfo(card.rarity); return (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded border" style={{ color: r.color, borderColor: r.color + "66", background: r.color + "14" }}>
                        {r.glyph && <span className="mr-1">{r.glyph}</span>}{card.rarity}
                      </span>); })()}
                    {!online && (
                      <span className="text-xs font-normal text-sky-400/90 border border-sky-500/40 px-2 py-0.5 rounded">
                        Hors ligne — cache
                      </span>
                    )}
                    {readOnly && (
                      <span className="text-xs font-normal text-zinc-400 border border-zinc-600 px-2 py-0.5 rounded">
                        Lecture seule
                      </span>
                    )}
                    {siblingIds && navIndex >= 0 && (
                      <span className="text-xs font-normal text-zinc-500">{navIndex + 1} / {siblingIds.length}</span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,300px)_1fr] gap-6 mt-5">
                  <div className="flex flex-col items-center min-w-0 w-full">
                    <CardImage card={card} quality="high" className="w-full max-w-[280px] rounded-lg shadow-2xl"
                      onErrorSrc={() => card.image_full ? "https://assets.tcgdex.net/univ/cardback.webp" : `${card.image}/low.webp`} />
                    <div className="mt-4 w-full space-y-2">
                      {/* Cardmarket : si le backend a pu construire le lien exact
                          (fiche produit), on propose Standard + Reverse ; sinon on
                          garde le lien de recherche par nom, qui marche partout. */}
                      {card.cardmarket_url_exact ? (
                        <div className="flex gap-2">
                          <a href={card.cardmarket_url_exact} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                              title="Fiche Cardmarket — offres en français">
                              <ExternalLink className="w-4 h-4 mr-2" />Cardmarket
                            </Button>
                          </a>
                          {/* Uniquement si la carte a VRAIMENT une reverse (card.variants.reverse) :
                              l'URL construite cote serveur cible specifiquement l'offre reverse
                              holo (isReverseHolo=Y), ce qui n'a pas de sens pour une Premiere
                              Edition (pas de reverse) ni verifie pour un holo simple - on
                              n'affiche donc ce bouton que dans le cas ou il est exact. */}
                          {variantFlags.reverse && card.cardmarket_url_exact_reverse && (
                            <a href={card.cardmarket_url_exact_reverse} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                                title="Fiche Cardmarket — offres reverse holo uniquement">
                                <ExternalLink className="w-4 h-4 mr-2" />Reverse
                              </Button>
                            </a>
                          )}
                          {/* Cartes Premiere Edition (vieux tirages WOTC, pas de reverse) :
                              `isFirstEd=Y` verifie en direct sur cardmarket.com le 2026-08-08
                              (parametre bidon de controle pour ecarter un effet de cache -
                              les annonces et le prix de depart changent bien reellement). */}
                          {variantFlags.firstEdition && card.cardmarket_url_exact_first_edition && (
                            <a href={card.cardmarket_url_exact_first_edition} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                                title="Fiche Cardmarket — offres 1re édition uniquement">
                                <ExternalLink className="w-4 h-4 mr-2" />1re Édition
                              </Button>
                            </a>
                          )}
                        </div>
                      ) : card.cardmarket_url && (
                        <a href={card.cardmarket_url} target="_blank" rel="noopener noreferrer" className="block">
                          <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                            <ExternalLink className="w-4 h-4 mr-2" />Cardmarket
                          </Button>
                        </a>
                      )}
                      {card.ebay_url && (
                        <div className="flex gap-2">
                          <a href={card.ebay_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700">
                              <ExternalLink className="w-4 h-4 mr-2" />eBay
                            </Button>
                          </a>
                          <a href={card.ebay_sold_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                            <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
                              title="Annonces vendues (prix réels)">
                              <ExternalLink className="w-4 h-4 mr-2" />eBay vendus
                            </Button>
                          </a>
                        </div>
                      )}
                    </div>
                    {cardmarket && (
                      <div className="mt-3 w-full bg-zinc-800/50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">Prix Cardmarket</span>
                          {cardmarket.updated && (
                            <span className="text-[10px] text-zinc-600" title="Dernière mise à jour TCGdex">
                              {cardmarket.updated.slice(0, 10)}
                            </span>
                          )}
                        </div>
                        {/* 5 colonnes ne tiennent pas sur un telephone : « Moy. »
                            et « 7j » sont masquees en dessous de `sm`, le
                            conteneur defilant restant un garde-fou pour que le
                            tableau n'elargisse jamais la fiche. */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-zinc-500">
                                <th className="text-left font-medium pb-1"></th>
                                <th className="text-right font-medium pb-1">Tend.</th>
                                <th className="text-right font-medium pb-1 hidden sm:table-cell">Moy.</th>
                                <th className="text-right font-medium pb-1 hidden sm:table-cell">7j</th>
                                <th className="text-right font-medium pb-1">30j</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-left text-emerald-400 py-0.5">Standard</td>
                                <td className="text-right font-semibold text-zinc-100">{eur(cardmarket.trend)}</td>
                                <td className="text-right text-zinc-300 hidden sm:table-cell">{eur(cardmarket.avg)}</td>
                                <td className="text-right text-zinc-300 hidden sm:table-cell">{eur(cardmarket.avg7)}</td>
                                <td className="text-right text-zinc-300">{eur(cardmarket.avg30)}</td>
                              </tr>
                              {hasReverse && (
                                <tr>
                                  <td className={`text-left py-0.5 ${SECONDARY_TEXT_CLASS[secondaryKey]}`}>{SECONDARY_LABEL[secondaryKey]}</td>
                                  <td className="text-right font-semibold text-zinc-100">{eur(cardmarket["trend-holo"])}</td>
                                  <td className="text-right text-zinc-300 hidden sm:table-cell">{eur(cardmarket["avg-holo"])}</td>
                                  <td className="text-right text-zinc-300 hidden sm:table-cell">{eur(cardmarket["avg7-holo"])}</td>
                                  <td className="text-right text-zinc-300">{eur(cardmarket["avg30-holo"])}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-3 pt-3 border-t border-zinc-700/50">
                          <PriceHistoryChart points={history}
                            secondaryLabel={SECONDARY_LABEL[secondaryKey]}
                            secondaryColor={SECONDARY_HEX[secondaryKey]} />
                        </div>
                      </div>
                    )}
                    {/* Évolution réel vs cote : sous le tableau Cardmarket, les
                        deux courbes de prix dans la même colonne (comble le
                        carré vide signalé le 2026-10-03). */}
                    <RealMarketBlock parts="chart" cardId={card.id} data={realMarket}
                      cote={cardmarket?.trend ?? cardmarket?.avg ?? null} />
                  </div>
                  <div className="space-y-5 min-w-0">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="info-tile">
                        <span className="info-label">Numéro</span>
                        <p className="info-value">#{card.localId} / {card.set?.cardCount?.official || card.set?.cardCount?.total}</p>
                      </div>
                      <div className="info-tile">
                        <span className="info-label">Collection</span>
                        <p className="info-value truncate" title={card.set?.name}>{card.set?.name}</p>
                      </div>
                      {card.types?.length > 0 && (
                        <div className="info-tile">
                          <span className="info-label">Type</span>
                          <p className="info-value">{card.types.join(", ")}</p>
                        </div>
                      )}
                      {card.category && (
                        <div className="info-tile">
                          <span className="info-label">Catégorie</span>
                          <p className="info-value">{card.category}</p>
                        </div>
                      )}
                      {card.illustrator && (
                        <div className="info-tile col-span-2">
                          <span className="info-label flex items-center gap-1">
                            <Paintbrush className="w-3 h-3" /> Illustrateur
                          </span>
                          {onBrowseIllustrator ? (
                            <button className="info-value text-left hover:text-red-400 hover:underline"
                              title={`Voir toutes les cartes illustrées par ${card.illustrator}`}
                              onClick={() => onBrowseIllustrator(card.illustrator)}>
                              {card.illustrator}
                            </button>
                          ) : (
                            <p className="info-value">{card.illustrator}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        Variations disponibles ({variants.length})
                        {!editMode && !readOnly && !card.variants_locked && (
                          <button onClick={startEdit} title="Corriger les variantes de cette carte"
                            className="ml-1 text-zinc-500 hover:text-white">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </h4>
                      {editMode ? (
                        <div>
                          <div className="text-xs text-zinc-500 mb-2">
                            Corrige une erreur de la carte, pour tous les comptes. Une carte garde au moins une
                            variante : sans aucune variante cochée ni variante perso, « Normale » est rétablie.
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {VARIANT_ORDER.map(type => {
                              const checked = editSelected.has(type);
                              return (
                                <button key={type} onClick={() => toggleEditType(type)}
                                  className={`variant-chip ${checked ? "bg-zinc-600 ring-2 ring-white/60" : "bg-zinc-800 opacity-60"}`}>
                                  {LABELS[type]}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-500"
                              onClick={saveOverride}>
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                            </Button>
                            <Button size="sm" disabled={saving} variant="outline" className="border-zinc-700 text-white"
                              onClick={resetOverride}>
                              <RotateCcw className="w-4 h-4 mr-1.5" />Réinitialiser (auto TCGdex)
                            </Button>
                            <Button size="sm" disabled={saving} variant="ghost" className="text-zinc-400 hover:text-white"
                              onClick={() => setEditMode(false)}>
                              <X className="w-4 h-4 mr-1" />Annuler
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {variants.map(v => {
                            const custom = isCustomVariant(v.type);
                            const style = variantStyle(v.type);
                            const isSelected = selectedVariantKey === v.key;
                            const vStatus = collection[getCollectionKey(card.id, v.type)];
                            return (
                              <button key={v.key} onClick={() => setSelectedVariantKey(v.key)}
                                className={`variant-chip ${custom ? "" : style.bg} ${isSelected ? "ring-2 ring-white/70" : ""}`}
                                style={custom ? { background: v.custom?.color } : undefined}
                                title={v.label}>
                                <span>{v.label}</span>
                                {vStatus === "owned"  && <CheckCircle2 className="w-3.5 h-3.5 ml-1.5" />}
                                {vStatus === "wanted" && <Star className="w-3.5 h-3.5 ml-1.5" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {!editMode && variants.length === 0 && <div className="text-sm text-zinc-500">Aucune variation connue</div>}
                    </div>
                    {selectedVariant && (readOnly ? (
                      <div className="flex items-center gap-2 text-sm">
                        {variantStatus === "owned" && (
                          <span className="flex items-center gap-1.5 text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" />Possédée
                          </span>
                        )}
                        {variantStatus === "wanted" && (
                          <span className="flex items-center gap-1.5 text-blue-400">
                            <Star className="w-4 h-4" />Recherchée
                          </span>
                        )}
                        {!variantStatus && <span className="text-zinc-500">Non possédée</span>}
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <Button
                          className={`flex-1 ${variantStatus === "owned" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-zinc-700 hover:bg-zinc-600"}`}
                          onClick={() => variantStatus === "owned"
                            ? onRemove(card.id, selectedVariant.type)
                            : onStatusChange(card.id, selectedVariant.type, "owned")}>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {variantStatus === "owned" ? "Retirer" : "Je possède"}
                        </Button>
                        <Button
                          className={`flex-1 ${variantStatus === "wanted" ? "bg-blue-500 hover:bg-blue-600" : "bg-zinc-700 hover:bg-zinc-600"}`}
                          onClick={() => variantStatus === "wanted"
                            ? onRemove(card.id, selectedVariant.type)
                            : onStatusChange(card.id, selectedVariant.type, "wanted")}>
                          <Star className="w-4 h-4 mr-2" />
                          {variantStatus === "wanted" ? "Retirer" : "Je recherche"}
                        </Button>
                      </div>
                    ))}
                    {selectedVariant && variantStatus === "owned" && (
                      <div>
                        <div className="text-xs text-zinc-500 mb-2">État de la carte</div>
                        {readOnly ? (
                          <div className="text-sm text-zinc-300">{variantCondition || "—"}</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {CONDITION_GRADES.map(grade => {
                              const active = variantCondition === grade;
                              return (
                                <Button key={grade} size="sm" variant="outline"
                                  className={active
                                    ? "bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500"
                                    : "border-zinc-700 text-white"}
                                  onClick={() => changeCondition(grade)}>
                                  {grade}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {!readOnly && onAddMasterRule && card && (
                      <div>
                        <div className="text-xs text-zinc-500 mb-2">
                          Réservé au Master Set + (ton compte seulement)
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {masterChip(null, "Toute la carte")}
                          {!masterOnly.includes("*") && variants.filter(v => v.type !== "loading" && !isCustomVariant(v.type))
                            .map(v => masterChip(v.type, v.label))}
                        </div>
                      </div>
                    )}
                    {!readOnly && !card.variants_locked && (
                      <div>
                        <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-amber-400" />Variantes perso sur cette carte
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {catalogBroken ? (
                            <span className="text-xs text-amber-400">
                              Tes variantes perso n'ont pas pu être chargées{customVariantsError ? ` (${customVariantsError})` : ""}.{" "}
                              <button className="underline hover:text-white" onClick={() => onReloadCustomVariants?.()}>Réessayer</button>
                            </span>
                          ) : customVariants.length === 0 && (
                            <span className="text-xs text-zinc-600">Aucune variante perso — crées-en une ci-dessous.</span>
                          )}
                          {customVariants.map(v => {
                            const active = cardCustomIds.has(v.id);
                            const removable = !!cardApp(v.id);
                            const inherited = active && !removable; // via une règle de set
                            return (
                              <button key={v.id} disabled={cvBusy || inherited}
                                onClick={() => toggleCardCustomVariant(v)}
                                title={inherited
                                  ? "Posée sur tout le set ou une rareté : se retire dans le set, bouton « Variantes perso »"
                                  : active ? "Cliquer pour retirer de cette carte" : "Cliquer pour ajouter à cette carte"}
                                className={`variant-chip ${active ? "" : "opacity-50"} ${inherited ? "cursor-not-allowed" : ""} ${removable ? "ring-2 ring-white/70" : ""}`}
                                style={{ background: active ? v.color : "transparent", border: `1.5px solid ${v.color}`, color: active ? "#fff" : v.color }}>
                                {v.abbr} · {v.name}{inherited ? " (set)" : ""}
                              </button>
                            );
                          })}
                        </div>
                        {customVariants.some(v => cardCustomIds.has(v.id) && !cardApp(v.id)) && (
                          <div className="text-xs text-zinc-500 mb-2">
                            « (set) » : posée sur tout le set ou une rareté. Elle se retire dans le set, bouton « Variantes perso ».
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <Input placeholder="Nouvelle variante (ex. Cosmos Holo)" value={cvNewName}
                            onChange={(e) => setCvNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") createCustomForCard(); }}
                            className="max-w-xs h-9 bg-zinc-800 border-zinc-700" />
                          <Button size="sm" variant="outline" className="border-zinc-700 text-white"
                            disabled={cvBusy || !cvNewName.trim()} onClick={createCustomForCard}>
                            Créer + ajouter
                          </Button>
                        </div>
                      </div>
                    )}
                    {!readOnly && selectedVariant && (
                      <AddToListButtons lists={lists}
                        onAddToList={addCurrentToList} onCreateList={onCreateList} />
                    )}
                    {/* Marché réel (Œil de Sauron) : dans la colonne de droite,
                        en largeur, pour rester à hauteur du tableau Cardmarket
                        de gauche au lieu d'allonger la colonne de l'image
                        (retour utilisateur du 2026-10-03). Le composant se
                        masque lui-même tant qu'aucune capture n'existe. La
                        cote de référence est la tendance Cardmarket « normale »
                        (les captures ne distinguent pas reverse/holo). */}
                    <RealMarketBlock wide parts="summary" cardId={card.id} data={realMarket}
                      cote={cardmarket?.trend ?? cardmarket?.avg ?? null}
                      onSeeAll={onOpenOeil ? () => { onClose(); onOpenOeil(); } : undefined} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <DialogHeader><DialogTitle className="sr-only">Erreur</DialogTitle></DialogHeader>
                <div className="text-center py-20 text-zinc-500">Erreur lors du chargement</div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
